import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// واجهة إعدادات بنك الصيانة
export interface MaintenanceBankSettings {
  imageProvider: 'imgbb' | 'imagekit';
  useImageStorageSettings?: boolean; // استخدام إعدادات تخزين الصور
  imgbbApiKey: string;
  imagekitPublicKey: string;
  imagekitPrivateKey: string;
  imagekitUrlEndpoint: string;
  maxImageSize: number; // بالميجابايت
  allowedFileTypes: string[];
  compressionEnabled: boolean;
  compressionQuality: number;
  autoBackup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  maxImagesPerTopic: number;
  requireApproval: boolean;
  allowPublicAccess: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

// الإعدادات الافتراضية
const defaultSettings: MaintenanceBankSettings = {
  imageProvider: 'imgbb',
  useImageStorageSettings: true, // استخدام إعدادات تخزين الصور افتراضياً
  imgbbApiKey: '',
  imagekitPublicKey: '',
  imagekitPrivateKey: '',
  imagekitUrlEndpoint: '',
  maxImageSize: 5,
  allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  compressionEnabled: true,
  compressionQuality: 85,
  autoBackup: true,
  backupFrequency: 'weekly',
  maxImagesPerTopic: 10,
  requireApproval: false,
  allowPublicAccess: true
};

/**
 * جلب إعدادات بنك الصيانة من Firebase
 */
export const getMaintenanceBankSettings = async (): Promise<MaintenanceBankSettings> => {
  try {
    console.log('📖 جلب إعدادات بنك الصيانة من Firebase...');
    
    const settingsRef = doc(db, 'settings', 'maintenanceBank');
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      const data = settingsSnap.data();
      console.log('✅ تم جلب إعدادات بنك الصيانة:', data);
      
      return {
        ...defaultSettings,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate()
      } as MaintenanceBankSettings;
    } else {
      console.log('📝 لا توجد إعدادات محفوظة، استخدام الإعدادات الافتراضية');
      return defaultSettings;
    }
  } catch (error) {
    console.error('❌ خطأ في جلب إعدادات بنك الصيانة:', error);
    return defaultSettings;
  }
};

/**
 * حفظ إعدادات بنك الصيانة في Firebase
 */
export const saveMaintenanceBankSettings = async (settings: MaintenanceBankSettings): Promise<void> => {
  try {
    console.log('💾 حفظ إعدادات بنك الصيانة في Firebase...');
    
    const settingsRef = doc(db, 'settings', 'maintenanceBank');
    
    // إضافة طوابع زمنية
    const settingsToSave = {
      ...settings,
      updatedAt: new Date(),
      createdAt: settings.createdAt || new Date()
    };
    
    await setDoc(settingsRef, settingsToSave, { merge: true });
    
    console.log('✅ تم حفظ إعدادات بنك الصيانة بنجاح');
  } catch (error) {
    console.error('❌ خطأ في حفظ إعدادات بنك الصيانة:', error);
    throw new Error('فشل في حفظ إعدادات بنك الصيانة');
  }
};

/**
 * التحقق من صحة إعدادات مزود الخدمة
 */
export const validateProviderSettings = (settings: MaintenanceBankSettings): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // إذا لم يكن يستخدم إعدادات تخزين الصور، نحقق من وجود مفاتيح API
  if (!settings.useImageStorageSettings) {
    if (settings.imageProvider === 'imgbb') {
      if (!settings.imgbbApiKey || settings.imgbbApiKey.trim() === '') {
        errors.push('مفتاح ImgBB API مطلوب');
      }
    } else if (settings.imageProvider === 'imagekit') {
      if (!settings.imagekitPublicKey || settings.imagekitPublicKey.trim() === '') {
        errors.push('مفتاح ImageKit العام مطلوب');
      }
      if (!settings.imagekitPrivateKey || settings.imagekitPrivateKey.trim() === '') {
        errors.push('مفتاح ImageKit الخاص مطلوب');
      }
      if (!settings.imagekitUrlEndpoint || settings.imagekitUrlEndpoint.trim() === '') {
        errors.push('رابط ImageKit مطلوب');
      }
    }
  }

  // التحقق من الحد الأقصى لحجم الصورة
  if (settings.maxImageSize < 1 || settings.maxImageSize > 10) {
    errors.push('الحد الأقصى لحجم الصورة يجب أن يكون بين 1 و 10 ميجابايت');
  }

  // التحقق من جودة الضغط
  if (settings.compressionEnabled && (settings.compressionQuality < 10 || settings.compressionQuality > 100)) {
    errors.push('جودة الضغط يجب أن تكون بين 10 و 100');
  }

  // التحقق من عدد الصور لكل موضوع
  if (settings.maxImagesPerTopic < 1 || settings.maxImagesPerTopic > 20) {
    errors.push('عدد الصور لكل موضوع يجب أن يكون بين 1 و 20');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * اختبار الاتصال بمزود الخدمة
 */
export const testProviderConnection = async (settings: MaintenanceBankSettings): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(`🔍 اختبار الاتصال بـ ${settings.imageProvider}...`);

    if (settings.imageProvider === 'imgbb') {
      // اختبار ImgBB
      let apiKey = settings.imgbbApiKey;

      // إذا كان يستخدم إعدادات تخزين الصور، جلب المفتاح من هناك
      if (settings.useImageStorageSettings) {
        try {
          const { getImgBBSettings } = await import('./imgbbService');
          const imgbbSettings = await getImgBBSettings();
          apiKey = imgbbSettings.apiKey;
        } catch (error) {
          return { success: false, message: 'فشل في جلب إعدادات ImgBB من تخزين الصور' };
        }
      }

      if (!apiKey) {
        return { success: false, message: 'مفتاح ImgBB API غير موجود' };
      }

      // يمكن إضافة اختبار فعلي هنا
      return { success: true, message: 'تم التحقق من مفتاح ImgBB بنجاح' };

    } else if (settings.imageProvider === 'imagekit') {
      // اختبار ImageKit
      let publicKey = settings.imagekitPublicKey;
      let privateKey = settings.imagekitPrivateKey;
      let urlEndpoint = settings.imagekitUrlEndpoint;

      // إذا كان يستخدم إعدادات تخزين الصور، جلب البيانات من هناك
      if (settings.useImageStorageSettings) {
        try {
          const { getImageKitSettings } = await import('./imagekitService');
          const imagekitSettings = await getImageKitSettings();
          publicKey = imagekitSettings.publicKey;
          privateKey = imagekitSettings.privateKey;
          urlEndpoint = imagekitSettings.urlEndpoint;
        } catch (error) {
          return { success: false, message: 'فشل في جلب إعدادات ImageKit من تخزين الصور' };
        }
      }

      if (!publicKey || !privateKey || !urlEndpoint) {
        return { success: false, message: 'بيانات ImageKit غير مكتملة' };
      }

      // يمكن إضافة اختبار فعلي هنا
      return { success: true, message: 'تم التحقق من بيانات ImageKit بنجاح' };
    }

    return { success: false, message: 'مزود خدمة غير مدعوم' };

  } catch (error) {
    console.error('❌ خطأ في اختبار الاتصال:', error);
    return { success: false, message: 'فشل في اختبار الاتصال' };
  }
};

/**
 * إعادة تعيين الإعدادات إلى القيم الافتراضية
 */
export const resetToDefaultSettings = async (): Promise<void> => {
  try {
    console.log('🔄 إعادة تعيين إعدادات بنك الصيانة إلى القيم الافتراضية...');
    
    await saveMaintenanceBankSettings(defaultSettings);
    
    console.log('✅ تم إعادة تعيين الإعدادات بنجاح');
  } catch (error) {
    console.error('❌ خطأ في إعادة تعيين الإعدادات:', error);
    throw new Error('فشل في إعادة تعيين الإعدادات');
  }
};

/**
 * تصدير الإعدادات كملف JSON
 */
export const exportSettings = (settings: MaintenanceBankSettings): void => {
  try {
    // إزالة المفاتيح الحساسة من التصدير
    const exportData = {
      ...settings,
      imgbbApiKey: settings.imgbbApiKey ? '***' : '',
      imagekitPrivateKey: settings.imagekitPrivateKey ? '***' : ''
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `maintenance-bank-settings-${new Date().toISOString().split('T')[0]}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log('✅ تم تصدير الإعدادات بنجاح');
  } catch (error) {
    console.error('❌ خطأ في تصدير الإعدادات:', error);
    throw new Error('فشل في تصدير الإعدادات');
  }
};

/**
 * استيراد الإعدادات من ملف JSON
 */
export const importSettings = (file: File): Promise<MaintenanceBankSettings> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const result = event.target?.result as string;
          const importedSettings = JSON.parse(result) as MaintenanceBankSettings;
          
          // دمج الإعدادات المستوردة مع الافتراضية
          const mergedSettings = {
            ...defaultSettings,
            ...importedSettings,
            // لا نستورد المفاتيح الحساسة
            imgbbApiKey: '',
            imagekitPrivateKey: ''
          };
          
          console.log('✅ تم استيراد الإعدادات بنجاح');
          resolve(mergedSettings);
        } catch (parseError) {
          console.error('❌ خطأ في تحليل ملف الإعدادات:', parseError);
          reject(new Error('ملف الإعدادات غير صالح'));
        }
      };
      
      reader.onerror = () => {
        console.error('❌ خطأ في قراءة الملف');
        reject(new Error('فشل في قراءة الملف'));
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('❌ خطأ في استيراد الإعدادات:', error);
      reject(new Error('فشل في استيراد الإعدادات'));
    }
  });
};
